import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, DownloadIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, RefreshCwIcon, EyeIcon, CalendarIcon, DollarSignIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
// Mock transaction data
const transactionsData = [{
  id: 'TX12345',
  amount: 1250.75,
  status: 'completed',
  customer: 'John Smith',
  time: '2023-10-28T10:30:00Z',
  method: 'QR Code',
  reference: 'REF-001',
  description: 'Purchase of electronics',
  location: 'Main Store'
}, {
  id: 'TX12346',
  amount: 750.25,
  status: 'completed',
  customer: 'Sarah Johnson',
  time: '2023-10-28T09:45:00Z',
  method: 'Card Payment',
  reference: 'REF-002',
  description: 'Purchase of appliances',
  location: 'Main Store'
}, {
  id: 'TX12347',
  amount: 2450.0,
  status: 'pending',
  customer: 'Robert Williams',
  time: '2023-10-28T09:15:00Z',
  method: 'QR Code',
  reference: 'REF-003',
  description: 'Purchase of smartphones',
  location: 'Downtown Branch'
}, {
  id: 'TX12348',
  amount: 450.5,
  status: 'failed',
  customer: 'Emily Brown',
  time: '2023-10-28T08:30:00Z',
  method: 'Card Payment',
  reference: 'REF-004',
  description: 'Purchase of accessories',
  location: 'Online Store'
}, {
  id: 'TX12349',
  amount: 1875.25,
  status: 'completed',
  customer: 'Michael Davis',
  time: '2023-10-27T16:45:00Z',
  method: 'QR Code',
  reference: 'REF-005',
  description: 'Purchase of televisions',
  location: 'Main Store'
}, {
  id: 'TX12350',
  amount: 325.5,
  status: 'completed',
  customer: 'Jessica Wilson',
  time: '2023-10-27T15:30:00Z',
  method: 'Card Payment',
  reference: 'REF-006',
  description: 'Purchase of headphones',
  location: 'Downtown Branch'
}, {
  id: 'TX12351',
  amount: 1200.0,
  status: 'completed',
  customer: 'David Taylor',
  time: '2023-10-27T14:15:00Z',
  method: 'QR Code',
  reference: 'REF-007',
  description: 'Purchase of laptops',
  location: 'Main Store'
}, {
  id: 'TX12352',
  amount: 550.75,
  status: 'refunded',
  customer: 'Jennifer Martinez',
  time: '2023-10-27T12:30:00Z',
  method: 'Card Payment',
  reference: 'REF-008',
  description: 'Purchase of speakers (refunded)',
  location: 'Online Store'
}, {
  id: 'TX12353',
  amount: 975.25,
  status: 'completed',
  customer: 'Christopher Anderson',
  time: '2023-10-26T16:45:00Z',
  method: 'QR Code',
  reference: 'REF-009',
  description: 'Purchase of cameras',
  location: 'Downtown Branch'
}, {
  id: 'TX12354',
  amount: 1500.0,
  status: 'completed',
  customer: 'Amanda Thomas',
  time: '2023-10-26T14:30:00Z',
  method: 'Card Payment',
  reference: 'REF-010',
  description: 'Purchase of gaming consoles',
  location: 'Main Store'
}];
interface Transaction {
  id: string;
  amount: number;
  status: string;
  customer: string;
  time: string;
  method: string;
  reference: string;
  description: string;
  location: string;
}
const MerchantTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>(transactionsData);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>(transactionsData);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');
  // Sorting state
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Detail view state
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  // Apply filters
  const applyFilters = () => {
    let result = transactions;
    // Search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(transaction => transaction.id.toLowerCase().includes(term) || transaction.customer.toLowerCase().includes(term) || transaction.reference.toLowerCase().includes(term) || transaction.description.toLowerCase().includes(term));
    }
    // Status filter
    if (statusFilter) {
      result = result.filter(transaction => transaction.status === statusFilter);
    }
    // Method filter
    if (methodFilter) {
      result = result.filter(transaction => transaction.method === methodFilter);
    }
    // Location filter
    if (locationFilter) {
      result = result.filter(transaction => transaction.location === locationFilter);
    }
    // Date range filter
    if (dateFromFilter) {
      const fromDate = new Date(dateFromFilter);
      result = result.filter(transaction => new Date(transaction.time) >= fromDate);
    }
    if (dateToFilter) {
      const toDate = new Date(dateToFilter);
      toDate.setHours(23, 59, 59, 999); // End of day
      result = result.filter(transaction => new Date(transaction.time) <= toDate);
    }
    // Amount range filter
    if (minAmountFilter) {
      const minAmount = parseFloat(minAmountFilter);
      result = result.filter(transaction => transaction.amount >= minAmount);
    }
    if (maxAmountFilter) {
      const maxAmount = parseFloat(maxAmountFilter);
      result = result.filter(transaction => transaction.amount <= maxAmount);
    }
    // Apply sorting if active
    if (sortField) {
      result = [...result].sort((a, b) => {
        const fieldA = a[sortField as keyof Transaction];
        const fieldB = b[sortField as keyof Transaction];
        if (typeof fieldA === 'string' && typeof fieldB === 'string') {
          return sortDirection === 'asc' ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
        } else {
          // Assuming numeric or date comparison
          return sortDirection === 'asc' ? fieldA < fieldB ? -1 : 1 : fieldB < fieldA ? -1 : 1;
        }
      });
    }
    setFilteredTransactions(result);
  };
  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Reset filters
  const resetFilters = () => {
    setStatusFilter('');
    setMethodFilter('');
    setLocationFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setMinAmountFilter('');
    setMaxAmountFilter('');
    setSearchTerm('');
  };
  // Handle view details
  const handleViewDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailModal(true);
  };
  // Handle export
  const handleExport = (format: string) => {
    console.log(`Exporting transactions as ${format}`);
    // Implementation for export functionality would go here
  };
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Format time
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  // Format date and time
  const formatDateTime = (dateString: string) => {
    return `${formatDate(dateString)} at ${formatTime(dateString)}`;
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-3815">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-3816" />
            Completed
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" data-id="element-3817">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-3818" />
            Pending
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" data-id="element-3819">
            <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-3820" />
            Failed
          </span>;
      case 'refunded':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" data-id="element-3821">
            <RefreshCwIcon className="w-3 h-3 mr-1" data-id="element-3822" />
            Refunded
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" data-id="element-3823">
            {status}
          </span>;
    }
  };
  // Effect to apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [searchTerm, statusFilter, methodFilter, locationFilter, dateFromFilter, dateToFilter, minAmountFilter, maxAmountFilter, sortField, sortDirection]);
  // Get unique locations for filter dropdown
  const uniqueLocations = Array.from(new Set(transactions.map(t => t.location)));
  return <div className="space-y-6" data-id="element-3824">
      <div data-id="element-3825">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3826">
          Transactions
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3827">
          View and manage your transaction history
        </p>
      </div>
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4" data-id="element-3828">
        <div className="relative flex-1" data-id="element-3829">
          <input type="text" placeholder="Search by ID, customer, reference or description..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-3830" />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3831">
            <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-3832" />
          </div>
        </div>
        <div className="flex gap-2" data-id="element-3833">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3834">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-3835" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <div className="relative" data-id="element-3836">
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => document.getElementById('export-menu')?.classList.toggle('hidden')} data-id="element-3837">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-3838" />
              Export
              <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-3839" />
            </button>
            <div id="export-menu" className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-10 hidden" data-id="element-3840">
              <button onClick={() => handleExport('csv')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-3841">
                Export as CSV
              </button>
              <button onClick={() => handleExport('xlsx')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-3842">
                Export as Excel
              </button>
              <button onClick={() => handleExport('pdf')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-3843">
                Export as PDF
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Advanced Filters */}
      {showFilters && <Card data-id="element-3844">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3845">
            <div data-id="element-3846">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3847">
                Status
              </label>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-3848">
                <option value="" data-id="element-3849">All Statuses</option>
                <option value="completed" data-id="element-3850">Completed</option>
                <option value="pending" data-id="element-3851">Pending</option>
                <option value="failed" data-id="element-3852">Failed</option>
                <option value="refunded" data-id="element-3853">Refunded</option>
              </select>
            </div>
            <div data-id="element-3854">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3855">
                Payment Method
              </label>
              <select value={methodFilter} onChange={e => setMethodFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-3856">
                <option value="" data-id="element-3857">All Methods</option>
                <option value="QR Code" data-id="element-3858">QR Code</option>
                <option value="Card Payment" data-id="element-3859">Card Payment</option>
              </select>
            </div>
            <div data-id="element-3860">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3861">
                Location
              </label>
              <select value={locationFilter} onChange={e => setLocationFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-3862">
                <option value="" data-id="element-3863">All Locations</option>
                {uniqueLocations.map(location => <option key={location} value={location} data-id="element-3864">
                    {location}
                  </option>)}
              </select>
            </div>
            <div data-id="element-3865">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3866">
                Date From
              </label>
              <div className="relative rounded-md shadow-sm" data-id="element-3867">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3868">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-3869" />
                </div>
                <input type="date" value={dateFromFilter} onChange={e => setDateFromFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-3870" />
              </div>
            </div>
            <div data-id="element-3871">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3872">
                Date To
              </label>
              <div className="relative rounded-md shadow-sm" data-id="element-3873">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3874">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-3875" />
                </div>
                <input type="date" value={dateToFilter} onChange={e => setDateToFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-3876" />
              </div>
            </div>
            <div data-id="element-3877">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-3878">
                Amount Range
              </label>
              <div className="flex space-x-2" data-id="element-3879">
                <div className="relative rounded-md shadow-sm flex-1" data-id="element-3880">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3881">
                    <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-3882" />
                  </div>
                  <input type="number" placeholder="Min" value={minAmountFilter} onChange={e => setMinAmountFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-3883" />
                </div>
                <div className="relative rounded-md shadow-sm flex-1" data-id="element-3884">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3885">
                    <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-3886" />
                  </div>
                  <input type="number" placeholder="Max" value={maxAmountFilter} onChange={e => setMaxAmountFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-3887" />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-3888">
            <button onClick={resetFilters} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-3889">
              Reset
            </button>
            <button onClick={() => setShowFilters(false)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-3890">
              Apply Filters
            </button>
          </div>
        </Card>}
      {/* Transactions Table */}
      <Card data-id="element-3891">
        <div className="mb-4 flex items-center justify-between" data-id="element-3892">
          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3893">
            Showing {filteredTransactions.length} transactions
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-3894">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3895">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-3896">
              <tr data-id="element-3897">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('id')} data-id="element-3898">
                  <div className="flex items-center" data-id="element-3899">
                    Transaction ID
                    {sortField === 'id' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-3900" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-3901" />)}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('amount')} data-id="element-3902">
                  <div className="flex items-center" data-id="element-3903">
                    Amount
                    {sortField === 'amount' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-3904" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-3905" />)}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('customer')} data-id="element-3906">
                  <div className="flex items-center" data-id="element-3907">
                    Customer
                    {sortField === 'customer' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-3908" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-3909" />)}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('time')} data-id="element-3910">
                  <div className="flex items-center" data-id="element-3911">
                    Date & Time
                    {sortField === 'time' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-3912" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-3913" />)}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('method')} data-id="element-3914">
                  <div className="flex items-center" data-id="element-3915">
                    Method
                    {sortField === 'method' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-3916" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-3917" />)}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')} data-id="element-3918">
                  <div className="flex items-center" data-id="element-3919">
                    Status
                    {sortField === 'status' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-3920" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-3921" />)}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3922">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3923">
              {filteredTransactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-3924">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-3925">
                    {transaction.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-3926">
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3927">
                    {transaction.customer}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3928">
                    {formatDate(transaction.time)}
                    <div className="text-xs text-gray-400 dark:text-gray-500" data-id="element-3929">
                      {formatTime(transaction.time)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3930">
                    {transaction.method}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3931">
                    {getStatusBadge(transaction.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3932">
                    <button onClick={() => handleViewDetails(transaction)} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300" data-id="element-3933">
                      <EyeIcon className="h-5 w-5" data-id="element-3934" />
                    </button>
                  </td>
                </tr>)}
              {filteredTransactions.length === 0 && <tr data-id="element-3935">
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-3936">
                    No transactions found matching your filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Transaction Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} title="Transaction Details" size="lg" data-id="element-3937">
        {selectedTransaction && <div className="space-y-6" data-id="element-3938">
            <div className="flex items-center justify-between" data-id="element-3939">
              <div data-id="element-3940">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-3941">
                  {selectedTransaction.id}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3942">
                  {formatDateTime(selectedTransaction.time)}
                </p>
              </div>
              <div data-id="element-3943">{getStatusBadge(selectedTransaction.status)}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3944">
              <div data-id="element-3945">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-3946">
                  Transaction Information
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2" data-id="element-3947">
                  <div className="flex justify-between" data-id="element-3948">
                    <span className="text-sm font-medium" data-id="element-3949">Amount:</span>
                    <span className="text-sm" data-id="element-3950">
                      {formatCurrency(selectedTransaction.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3951">
                    <span className="text-sm font-medium" data-id="element-3952">Payment Method:</span>
                    <span className="text-sm" data-id="element-3953">
                      {selectedTransaction.method}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3954">
                    <span className="text-sm font-medium" data-id="element-3955">Reference:</span>
                    <span className="text-sm" data-id="element-3956">
                      {selectedTransaction.reference}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3957">
                    <span className="text-sm font-medium" data-id="element-3958">Location:</span>
                    <span className="text-sm" data-id="element-3959">
                      {selectedTransaction.location}
                    </span>
                  </div>
                </div>
              </div>
              <div data-id="element-3960">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-3961">
                  Customer Information
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2" data-id="element-3962">
                  <div className="flex justify-between" data-id="element-3963">
                    <span className="text-sm font-medium" data-id="element-3964">Customer Name:</span>
                    <span className="text-sm" data-id="element-3965">
                      {selectedTransaction.customer}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-3966">
                    <span className="text-sm font-medium" data-id="element-3967">Description:</span>
                    <span className="text-sm" data-id="element-3968">
                      {selectedTransaction.description}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-3969">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-3970">
                Actions
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-3971">
                <button className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-sm" data-id="element-3972">
                  Download Receipt
                </button>
                {selectedTransaction.status === 'completed' && <button className="px-3 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-md text-sm" data-id="element-3973">
                    Request Refund
                  </button>}
                <button className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md text-sm" data-id="element-3974">
                  Report Issue
                </button>
              </div>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default MerchantTransactions;